import {
	normalizeCaseFilter,
} from "./caseFilterNormalization.mjs";


export {
	normalizeCaseFilter,
};


const PRIMARY_FILTER_LABELS = {
	all:
		"All",

	solution:
		"Solutions",

	work:
		"Work",
};


const CASE_SCOPE_FILTER_VALUES =
	new Set([
		"all",
		"work",
		"solution",
	]);


const normalizeDisplayLabel = (
	value,
) => {
	return String(
		value ??
			"",
	)
		.trim()
		.replace(
			/\s+/g,
			" ",
		);
};


const getRawCaseFilters = (
	caseItem,
) => {
	if (
		Array.isArray(
			caseItem?.filters,
		)
	) {
		return caseItem.filters;
	}


	if (
		typeof caseItem?.filters ===
			"string"
	) {
		return caseItem.filters.split(
			",",
		);
	}


	return [];
};


const getRawCaseTags = (
	caseItem,
) => {
	if (
		Array.isArray(
			caseItem?.tags,
		)
	) {
		return caseItem.tags;
	}


	if (
		typeof caseItem?.tags ===
			"string"
	) {
		return caseItem.tags.split(
			",",
		);
	}


	return [];
};


export const isCaseScopeFilter = (
	filter,
) => {
	return CASE_SCOPE_FILTER_VALUES.has(
		normalizeCaseFilter(
			filter,
		),
	);
};


export const getCaseFilterOptions = (
	caseItem,
) => {
	const seen =
		new Set();


	return getRawCaseFilters(
		caseItem,
	)
		.map(
			(
				rawFilter,
			) => {
				const label =
					normalizeDisplayLabel(
						rawFilter,
					);

				const value =
					normalizeCaseFilter(
						rawFilter,
					);


				return {
					value,
					label,
				};
			},
		)
		.filter(
			(filter) => {
				if (
					!filter.label ||
					filter.value ===
						"all" ||
					seen.has(
						filter.value,
					)
				) {
					return false;
				}


				seen.add(
					filter.value,
				);


				return true;
			},
		);
};


/*
 * ============================================================
 * TOPIC FILTERS
 * ============================================================
 *
 * Filters from the Sheet excluding:
 *
 * all
 * work
 * solution
 *
 * Those values are scope/type information rather than
 * topical categories.
 */

export const getCaseTopicFilterOptions =
	(caseItem) => {
		return getCaseFilterOptions(
			caseItem,
		).filter(
			(filter) =>
				!isCaseScopeFilter(
					filter.value,
				),
		);
	};


/*
 * The filter used for compact case presentation:
 *
 * Fortnite
 * Choiz.io
 * Unreal Engine
 * etc.
 *
 * Sheet order is preserved.
 */

export const getFirstCaseTopicFilterOption =
	(caseItem) => {
		return (
			getCaseTopicFilterOptions(
				caseItem,
			)?.[0] ??
			null
		);
	};


export const getCaseSecondaryFilters = (
	caseItem,
) => {
	return getCaseFilterOptions(
		caseItem,
	).map(
		(filter) =>
			filter.value,
	);
};


/*
 * ============================================================
 * DISPLAY TAGS
 * ============================================================
 *
 * Returns the old "tags" field after removing information
 * already represented by:
 *
 * - topic filters
 * - Work / Solutions
 *
 * Example:
 *
 * filters:
 * "Fortnite, Unreal Engine"
 *
 * tags:
 * "Fortnite, Realtime, Unreal Engine, Campaign"
 *
 * becomes:
 *
 * [
 * 		"Realtime",
 * 		"Campaign",
 * ]
 *
 * Original display text and ordering are preserved.
 */

export const getCaseDisplayTags = (
	caseItem,
) => {
	const filterValues =
		new Set(
			getCaseFilterOptions(
				caseItem,
			).map(
				(filter) =>
					filter.value,
			),
		);


	const normalizedType =
		normalizeCaseFilter(
			caseItem?.type,
		);


	const seen =
		new Set();


	return getRawCaseTags(
		caseItem,
	)
		.map(
			(
				rawTag,
			) => {
				const label =
					normalizeDisplayLabel(
						rawTag,
					);

				const value =
					normalizeCaseFilter(
						rawTag,
					);


				return {
					value,
					label,
				};
			},
		)
		.filter(
			(tag) => {
				if (
					!tag.label ||
					tag.value ===
						"all"
				) {
					return false;
				}


				/*
				 * Already represented by the
				 * Work / Solutions pill.
				 */

				if (
					tag.value ===
						normalizedType ||
					tag.value ===
						"work" ||
					tag.value ===
						"solution"
				) {
					return false;
				}


				/*
				 * Already represented by one
				 * of the Sheet filters.
				 *
				 * We remove ALL matching
				 * filters here, not just the
				 * first displayed filter.
				 */

				if (
					filterValues.has(
						tag.value,
					)
				) {
					return false;
				}


				if (
					seen.has(
						tag.value,
					)
				) {
					return false;
				}


				seen.add(
					tag.value,
				);


				return true;
			},
		)
		.map(
			(tag) =>
				tag.label,
		);
};


export const caseMatchesFilter = (
	caseItem,
	filter,
) => {
	const normalizedFilter =
		normalizeCaseFilter(
			filter,
		);


	if (
		normalizedFilter ===
			"all"
	) {
		return true;
	}


	const normalizedType =
		normalizeCaseFilter(
			caseItem?.type,
		);


	if (
		normalizedType ===
			normalizedFilter
	) {
		return true;
	}


	return getCaseSecondaryFilters(
		caseItem,
	).includes(
		normalizedFilter,
	);
};


export const getAvailableCaseFilters = (
	data = [],
) => {
	const filters =
		new Set();


	data.forEach(
		(caseItem) => {
			const type =
				normalizeCaseFilter(
					caseItem?.type,
				);


			if (
				type !==
					"all"
			) {
				filters.add(
					type,
				);
			}


			getCaseSecondaryFilters(
				caseItem,
			).forEach(
				(filter) => {
					filters.add(
						filter,
					);
				},
			);
		},
	);


	return Array.from(
		filters,
	);
};


export const getAvailableCaseFilterOptions =
	(data = []) => {
		const options =
			new Map();


		const addOption =
			(
				value,
				label,
			) => {
				const normalizedValue =
					normalizeCaseFilter(
						value,
					);


				if (
					normalizedValue ===
						"all" ||
					options.has(
						normalizedValue,
					)
				) {
					return;
				}


				options.set(
					normalizedValue,
					{
						value:
							normalizedValue,

						label:
							normalizeDisplayLabel(
								label,
							) ||
							getFilterLabel(
								normalizedValue,
							),
					},
				);
			};


		data.forEach(
			(caseItem) => {
				const type =
					normalizeCaseFilter(
						caseItem?.type,
					);


				if (
					type !==
						"all"
				) {
					addOption(
						type,
						getFilterLabel(
							type,
						),
					);
				}


				getCaseFilterOptions(
					caseItem,
				).forEach(
					(filter) => {
						addOption(
							filter.value,
							filter.label,
						);
					},
				);
			},
		);


		return Array.from(
			options.values(),
		);
	};


export const getFilterLabel = (
	filter,
) => {
	const normalizedFilter =
		normalizeCaseFilter(
			filter,
		);


	if (
		PRIMARY_FILTER_LABELS[
			normalizedFilter
		]
	) {
		return PRIMARY_FILTER_LABELS[
			normalizedFilter
		];
	}


	return normalizedFilter
		.split(
			"-",
		)
		.filter(
			Boolean,
		)
		.map(
			(word) =>
				word
					.charAt(
						0,
					)
					.toUpperCase() +
				word.slice(
					1,
				),
		)
		.join(
			" ",
		);
};


export const getFilterOverviewPath = (
	filter,
) => {
	const normalizedFilter =
		normalizeCaseFilter(
			filter,
		);


	if (
		normalizedFilter ===
			"all"
	) {
		return "/cases";
	}


	if (
		normalizedFilter ===
			"solution"
	) {
		return "/cases/solutions";
	}


	return `/cases/${normalizedFilter}`;
};